// tempexp_18907-full-file
import { Icon20AlarmOutline, Icon24ClockOutline } from '@vkontakte/icons';
import { classNames, Spacing, Text } from '@vkontakte/vkui';
import happyByte from 'img/discount-in-outflow.png';
import sadByte from 'img/sad-byte-head.png';
import React, { memo, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { emitAnalyticEvent } from 'reactApp/appHelpers/experimentAnalytic';
import { AnalyticEventNames } from 'reactApp/appHelpers/experimentAnalytic/eventNames';
import { discountInOutflowValue } from 'reactApp/appHelpers/featuresHelpers/features/discountInOutflow';
import { WhatsNewDialog } from 'reactApp/components/WhatsNewDialog/WhatsNewDialog';
import type { BillingDataSubscriptionInfo } from 'reactApp/modules/billingData/billingData.types';
import { CardActions } from 'reactApp/modules/creditCard/creditCard.module';
import { CardSelectors } from 'reactApp/modules/creditCard/creditCard.selectors';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { SubscriptionRenewalActions } from 'reactApp/modules/subscriptionRenewal/subscriptionRenewal.module';
import { isCurrentDarkTheme } from 'reactApp/modules/theme/theme.selectors';
import { useSendSubscriptionModalAnalytics } from 'reactApp/sections/SubscriptionsPage/ui/SubscriptionModal/SubscriptionModal.analytics';
import {
    type DiscountInOutflowLSData,
    DISCOUNT_IN_OUTFLOW,
    DISCOUNT_IN_OUTFLOW_PERIODS_TABLE,
    discountInOutflowTextFormatter,
    discountInOutflowTypedRadarName,
} from 'reactApp/ui/DiscountInOutflow/DiscountInOutflow.const';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { daysPluralNominative } from 'reactApp/utils/pluralHelpers';

import styles from './DiscountInOutflowSaveModal.css';
import { closeDiscountInOutflowSaveModal } from './DiscountInOutflowSaveModal.helpers';

export enum DiscountInOutflowSaveModalType {
    start = 'start',
    base = 'base',
    last = 'last',
}
export interface DiscountInOutflowSaveModalProps {
    analyticType: DiscountInOutflowSaveModalType;
    availabilityPeriod: number;
    subscription?: BillingDataSubscriptionInfo;
    onShow?: () => void;
    forceDarkTheme?: boolean;
}

const decoration = (
    <svg width="75" height="83" viewBox="0 0 75 83" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M46.9552 45.1053C44.0599 42.5835 37.5629 37.2998 33.7803 34.0034" strokeWidth="5.70341" strokeLinecap="square" />
        <path d="M56.6267 36.1325L53.9494 26.0226" strokeWidth="5.70341" strokeLinecap="square" />
        <path d="M42.0295 57.4226L31.7771 59.4884" strokeWidth="5.70341" strokeLinecap="square" />
    </svg>
);

export interface ImageBannerProps {
    isExpires: boolean;
    availabilityPeriod: number;
    subscription?: BillingDataSubscriptionInfo;
    productPeriod: string;
    originalPrice: number;
    discountPrice?: number;
}

const ImageBanner = memo<ImageBannerProps>(
    ({ isExpires, availabilityPeriod, subscription, productPeriod, originalPrice, discountPrice }) => {
        return (
            <div className={styles.imageBanner}>
                <div className={styles.decoration}>{decoration}</div>
                <div className={classNames(styles.availabilityPeriodInfo, { [styles.expires]: isExpires })}>
                    {isExpires ? <Icon20AlarmOutline width={24} height={24} /> : <Icon24ClockOutline />}
                    <Text className={styles.text}>
                        {isExpires ? 'последний день' : `${availabilityPeriod} ${daysPluralNominative(availabilityPeriod)}`}
                    </Text>
                </div>
                <Text className={styles.valueText}>+{subscription?.space.value}</Text>
                <Spacing size={12} />
                <Text className={styles.priceContainer}>
                    <span className={styles.oldPrice}>{discountInOutflowTextFormatter.format(originalPrice || 0)} ₽</span>
                    <span className={styles.price}>{discountInOutflowTextFormatter.format(discountPrice || 0)} ₽</span>
                    <span className={styles.period}>&nbsp;{DISCOUNT_IN_OUTFLOW_PERIODS_TABLE[productPeriod]}</span>
                </Text>
            </div>
        );
    }
);

const useContent = ({ isExpires, subscription, availabilityPeriod }) => {
    const title = useMemo(
        () =>
            isExpires ? (
                <>
                    Последний день скидки 30% на{'\u00A0'}ваш тариф Mail{'\u00A0'}Space — успейте её{'\u00A0'}забрать
                </>
            ) : (
                <>
                    Ещё{'\u00A0'}есть {availabilityPeriod}
                    {'\u00A0'}
                    {daysPluralNominative(availabilityPeriod)}, чтобы{'\u00A0'}активировать скидку {discountInOutflowValue}% на
                    {'\u00A0'}ваш{'\u00A0'}тариф Mail
                    {'\u00A0'}Space
                </>
            ),
        [isExpires, availabilityPeriod]
    );

    const content = useMemo(
        () =>
            isExpires ? (
                <Text className={styles.text}>
                    Продлите подписку. У{'\u00A0'}вас{'\u00A0'}будет {subscription.space.value} в{'\u00A0'}Облаке, и{'\u00A0'}совсем без
                    {'\u00A0'}рекламы. Ещё и{'\u00A0'}сэкономите
                </Text>
            ) : (
                <Text className={styles.text}>
                    С{'\u00A0'}подпиской у{'\u00A0'}вас{'\u00A0'}будет {subscription.space.value} в{'\u00A0'}Облаке и{'\u00A0'}никакой
                    рекламы даже в{'\u00A0'}Почте. Ещё и{'\u00A0'}сэкономите
                </Text>
            ),
        [isExpires, subscription]
    );

    return { title, content };
};

export const DiscountInOutflowSaveModal = memo<DiscountInOutflowSaveModalProps>(
    ({ analyticType: type, forceDarkTheme = false, availabilityPeriod, onShow, subscription }) => {
        const discountInOutflowData: DiscountInOutflowLSData = useMemo(() => {
            return storeHelper.getValue(DISCOUNT_IN_OUTFLOW) || {};
        }, []);

        const isDarkTheme = useSelector(isCurrentDarkTheme);
        const isExpires = availabilityPeriod === 0;
        const { card, isLoading: isCardLoading } = useSelector(CardSelectors.getCard);
        const dispatch = useDispatch();
        const sendAnalytics = useSendSubscriptionModalAnalytics(subscription as any);

        const typedRadarName = discountInOutflowTypedRadarName(type);

        useEffect(() => {
            dispatch(CardActions.loadCardIfNotLoaded());
        }, []);

        useEffect(() => {
            onShow?.();
            sendPaymentGa({
                action: 'show',
                eventCategory: ECategoryGa.modal,
                name: typedRadarName,
            });
            emitAnalyticEvent(AnalyticEventNames.DISCOUNT_MODAL_SHOW, { type });
        }, []);

        const onCancelAction = () => {
            sendPaymentGa({
                action: 'cancel',
                eventCategory: ECategoryGa.modal,
                name: typedRadarName,
            });
            closeDiscountInOutflowSaveModal();
            emitAnalyticEvent(AnalyticEventNames.DISCOUNT_MODAL_CANCEL, { type });
        };

        const onClickAction = useCallback(() => {
            dispatch(
                SubscriptionRenewalActions.addAutoRenewWithDiscount({
                    sendAnalytics,
                    card,
                    subscription: subscription as any,
                    discountModalType: type,
                })
            );
            sendPaymentGa({
                action: 'click',
                eventCategory: ECategoryGa.modal,
                name: typedRadarName,
            });
            emitAnalyticEvent(AnalyticEventNames.DISCOUNT_MODAL_CLICK, { type });
        }, [card, subscription]);

        const { content, title } = useContent({ isExpires, subscription, availabilityPeriod });
        const themeStyle = forceDarkTheme || isDarkTheme ? styles.dark : styles.light;

        return (
            <WhatsNewDialog
                imageUrl={isExpires ? sadByte : happyByte}
                imageWrapperClassName={classNames(styles.imageWrapper, themeStyle, {
                    [styles.expires]: isExpires,
                })}
                imageClassName={styles.image}
                title={title}
                content={
                    <div className={styles.content}>
                        <Spacing size={12} />
                        {content}
                        <Spacing size={24} />
                    </div>
                }
                imageAdditionalContent={
                    <ImageBanner
                        isExpires={isExpires}
                        availabilityPeriod={availabilityPeriod}
                        subscription={subscription}
                        discountPrice={discountInOutflowData.discountPrice}
                        productPeriod={discountInOutflowData.period}
                        originalPrice={discountInOutflowData.originalPrice}
                    />
                }
                primaryButtonText="Продлить со скидкой"
                secondaryButtonText="Не сейчас"
                isPrimaryDisabled={isCardLoading}
                onSecondary={onCancelAction}
                onDismiss={onCancelAction}
                onClick={onClickAction}
                imageBgColor="var(--vkui--color_background_modal)"
                buttonTheme="vk"
                dialogSize="tiny"
                qaId="discount-in-outflow-save-modal"
                isRebranding
            />
        );
    }
);

DiscountInOutflowSaveModal.displayName = 'DiscountInOutflowSaveModal';
ImageBanner.displayName = 'ImageBanner';
